import { z } from 'zod';
/**
 * Per-fan × per-character affinity (好感度) capability. Score 0-100,
 * mapped to 6 named levels. Stored in `nx-fan-affinity-<env>` keyed
 * on (fanId, characterId).
 *
 * The level is derived from value via a fixed table (see
 * `levelFromValue` in admin-api/src/lib/fan-affinity.ts). We persist
 * `level` alongside `value` because every UI list-render filters by
 * level and the level-cross detection wants the prior level in the
 * same atomic write.
 */
export const LevelNameSchema = z.enum([
    '陌生',
    '點頭之交',
    '朋友',
    '親近',
    '摯友',
    '靈魂相依',
]);
/** Index → name. Matches the threshold table in the spec. */
export const LEVEL_NAMES = [
    '陌生', '點頭之交', '朋友', '親近', '摯友', '靈魂相依',
];
/**
 * Per `fan-affinity-v2` capability — 10 tiers of equal 10-point width.
 * tier_n covers score range [10*(n-1)+1 .. 10*n] for n in 1..10, with
 * score 0 clamped to tier_1. Score 50 = tier_5 (neutral baseline).
 *
 * Names are placeholders; refine before Phase 3 flip per design.md
 * Open Question #5.
 */
export const TIER_NAMES_V2 = [
    '陌路', '面熟', '搭過話', '聊得來', '中立 / 普通熟識',
    '熟悉的朋友', '常聊的好友', '親密知己', '深度信任', '極親密',
];
/**
 * Per-tier qualitative description. Used by:
 *   - Fan SPA AffinityBar explainer modal (tells the fan what the
 *     current relationship state feels like)
 *   - Admin "粉絲關係" tab info popover + tier legend
 *
 * Kept short (≤ ~40 chars each) so it reads well inline. Tone is
 * fan-friendly — never mentions "score" or "delta" — and matches the
 * chat-injection guidance that the analyzer uses for the same tier.
 */
export const TIER_DESCRIPTIONS_V2 = [
    '幾乎不認得，AI 的回應較禮貌克制。', // 1 陌路
    '見過幾次面，AI 的態度仍以客套為主。', // 2 面熟
    '稍微熟一點，AI 願意分享些表面資訊。', // 3 搭過話
    '聊得來，AI 會主動回應你的好奇心。', // 4 聊得來
    '中性熟識，AI 會自然對話但保持距離。', // 5 中立 / 普通熟識
    '熟悉的朋友，AI 願意分享更多日常。', // 6 熟悉的朋友
    '常聊的好友，AI 把你當成重要的人。', // 7 常聊的好友
    '親密知己，AI 會分享心情、敏感話題不再迴避。', // 8 親密知己
    '深度信任，AI 對你開放最內在的想法。', // 9 深度信任
    '極親密，AI 把你視為人生中無可取代的存在。', // 10 極親密
];
/** Pure: tier index (1-10) → its description string. Clamped. */
export function tierDescription(tier) {
    const idx = Math.max(1, Math.min(10, Math.floor(tier))) - 1;
    return TIER_DESCRIPTIONS_V2[idx] ?? '';
}
export function resolveTier(score, characterTiers) {
    const clamped = Math.max(0, Math.min(100, Math.floor(score)));
    const tier = clamped === 0 ? 1 : Math.min(10, Math.ceil(clamped / 10));
    const idx = tier - 1;
    const override = characterTiers && characterTiers.length === 10 ? characterTiers[idx] : null;
    return {
        tier,
        name: override?.name ?? TIER_NAMES_V2[idx] ?? `第 ${tier} 階`,
        description: override?.description ?? TIER_DESCRIPTIONS_V2[idx] ?? '',
    };
}
/** Single level-crossing event appended to the milestones array. */
export const FanAffinityMilestoneSchema = z.object({
    /** 0-5 — the level the fan JUST crossed INTO. */
    level: z.number().int().min(0).max(5),
    levelName: LevelNameSchema,
    crossedAt: z.string().datetime(),
    /** Optional ~40-char snippet of what triggered the cross (last chat
     *  reply / mini-app completion / etc.). Free-form, no PII guarantee. */
    context: z.string().max(80).optional(),
});
/** A row in `nx-fan-affinity-<env>`. */
export const FanAffinityRowSchema = z.object({
    fanId: z.string().min(1),
    characterId: z.string().min(1),
    /**
     * Integer in [0, 100]. **v2 semantics**: `50` is the neutral baseline.
     * Scores below 50 represent negative relationship; scores above 50
     * represent positive. v1 records (where 0 was neutral) get linearly
     * remapped to [50, 100] via the migration script.
     */
    value: z.number().int().min(0).max(100),
    /** Legacy v1 field: integer in [0, 5]. Retained for back-compat
     *  during migration; new code SHOULD prefer `tier` (1-10). */
    level: z.number().int().min(0).max(5),
    /** **v2** tier — integer in [1, 10] derived from value via
     *  `tierOfScore` in admin-api/src/lib/fan-affinity.ts. Optional
     *  during migration; populated for every row after schema v2. */
    tier: z.number().int().min(1).max(10).optional(),
    /** Schema migration marker. `2` after the v2 migration script
     *  runs. Absent / `1` on pre-migration rows. */
    schemaVersion: z.number().int().min(1).optional(),
    /** ISO datetime of the most recent analyzer pass over this pair.
     *  Used by the analyzer to filter "pairs with new turns since". */
    lastAnalyzedAt: z.string().datetime().optional(),
    /** Append-only timeline of level crossings. Capped at 10 to bound
     *  row size; v1 has only 6 levels (0-5) so the cap is generous. */
    milestones: z.array(FanAffinityMilestoneSchema).max(10),
    /** ISO timestamp of the most recent creditAffinity call. */
    lastInteractionAt: z.string().datetime(),
    /** Reserved for the eventual decay sweep. v1 unused. */
    lastDecayCheckAt: z.string().datetime().optional(),
    /** Current consecutive-day-interaction streak. */
    streakDays: z.number().int().min(0),
    /** UTC date string (YYYY-MM-DD) of the most recent interaction —
     *  used to decide streak continuation vs break in creditAffinity. */
    streakLastDay: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    /** UTC date string (YYYY-MM-DD) of the fan's most recent daily-
     *  greet response. Absent for fans who haven't responded to a
     *  greet yet. Drives the once-per-day cap on the +2 credit. */
    lastDailyGreetAt: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    /** UTC date string of the most recent mood check-in. Drives the
     *  once-per-day cap on the +1 mood-checkin credit. */
    lastMoodCheckinAt: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    /** Most recent mood check-in payload, preserved so the UI can
     *  show "今天你選了 😴" after submission. Optional + bounded. */
    lastMoodEmoji: z.string().max(8).optional(),
    lastMoodNote: z.string().max(160).optional(),
    /** Rolling history of the time-of-day bucket (morning / daytime /
     *  evening / night) when each recent interaction landed. Capped at
     *  50 entries (~12 days at 4 interactions/day). Drives the
     *  personalized-greeting feature (iter 29) — when ≥60% of recent
     *  interactions cluster in one bucket, the daily greet switches
     *  to that bucket's voice regardless of the actual UTC hour. */
    recentBuckets: z.array(z.string().max(16)).max(50).optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
/**
 * Weekly dashboard summary returned alongside the relationship list
 * — the "連續登入儀錶板" piece of fan-affinity-system. Aggregates
 * across all the fan's relationships into a 7-day check-in grid +
 * a longest-current-streak + the streak multiplier the longest
 * streak earns.
 */
export const WeeklyDashboardSchema = z.object({
    /** 7 entries, oldest → newest (today is the last entry). Each
     *  has the UTC date and whether the fan interacted with ANY
     *  character that day. */
    week: z.array(z.object({
        date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
        active: z.boolean(),
    })).length(7),
    /** Longest currently-active streak (in days) across all the
     *  fan's relationships. Drives the displayed multiplier. */
    longestStreak: z.number().int().min(0),
    /** Streak bonus multiplier the longestStreak earns
     *  (1.0 / 1.2 / 1.5 / 2.0). */
    currentMultiplier: z.number(),
});
/** Shape of items in GET /api/fan/relationships. */
export const RelationshipListItemSchema = z.object({
    characterId: z.string(),
    characterName: z.string(),
    value: z.number().int().min(0).max(100),
    level: z.number().int().min(0).max(5),
    levelName: LevelNameSchema,
    lastInteractionAt: z.string().datetime(),
    streakDays: z.number().int().min(0),
    /** First ~120 chars of the first-person memory card, or null when no
     *  card has been generated yet (regen Lambda hasn't run / fan is
     *  brand new). */
    memoryCardPreview: z.string().nullable(),
    milestoneCount: z.number().int().min(0),
});
/** Shape of GET /api/fan/relationships/{characterId}. */
export const RelationshipDetailSchema = z.object({
    characterId: z.string(),
    characterName: z.string(),
    value: z.number().int().min(0).max(100),
    level: z.number().int().min(0).max(5),
    levelName: LevelNameSchema,
    /** Distance to next level, or null when already at level 5. */
    valueToNextLevel: z.number().int().min(0).nullable(),
    /** [lo, hi] inclusive range of the current level — UI uses this to
     *  draw the progress bar within the current segment. */
    valueRangeOfCurrentLevel: z.tuple([z.number().int(), z.number().int()]),
    streakDays: z.number().int().min(0),
    lastInteractionAt: z.string().datetime(),
    /** Full first-person memory card. Null when none generated. */
    memoryCard: z.string().nullable(),
    memoryCardUpdatedAt: z.string().datetime().nullable(),
    milestones: z.array(FanAffinityMilestoneSchema),
    /** True if the fan hasn't yet responded to today's daily greeting
     *  from this character (drives the "今日的問候" card on
     *  RelationshipDetail). */
    dailyGreetAvailable: z.boolean(),
    /** Pre-generated greeting message the character is "saying" to
     *  the fan today. Always present when dailyGreetAvailable=true;
     *  null when already greeted today. */
    dailyGreetMessage: z.string().nullable(),
    /** Reward base amount the fan earns when responding (2 for a
     *  regular day, 3 for a known holiday). */
    dailyGreetReward: z.number().int().min(0),
    /** Active holiday id when today is in the fan-holidays calendar,
     *  null otherwise. Drives UI theming (gold accent + holiday
     *  emoji on the greeting card). */
    dailyGreetHolidayId: z.string().nullable(),
    dailyGreetHolidayName: z.string().nullable(),
    dailyGreetHolidayEmoji: z.string().nullable(),
    /** True if the fan hasn't yet submitted a mood check-in today. */
    moodCheckinAvailable: z.boolean(),
    /** Snapshot of today's submitted mood, when already done. Used
     *  to keep the card visible (in a read-only state) after submit. */
    todayMoodEmoji: z.string().nullable(),
    todayMoodNote: z.string().nullable(),
    /** When ≥60% of the fan's recent interactions cluster in one
     *  time-of-day bucket and the current real-time bucket differs,
     *  the daily greet is generated from the PREFERRED bucket instead.
     *  These two fields surface the personalization so the UI can show
     *  a "💡 已根據你的互動習慣調整" hint. Both null when no clear
     *  pattern is detected (need ≥10 interactions). */
    personalizedGreetingBucket: z.string().nullable(),
    personalizedGreetingHint: z.string().nullable(),
    /** Unlock packs the fan has reached based on the level milestones.
     *  Each level 1-5 unlocks one pack (conversationStarter +
     *  characterQuote + stickerEmoji). Level 0 has no pack — it's the
     *  starting state. The array is derived server-side from
     *  milestones[] (or "all up to current level" if no milestone
     *  history is available). */
    /**
     * Per `fan-affinity-v2` (2026-05) — resolved tier name + description
     * for the AffinityBar chip. Server-side resolves these honouring the
     * operator's per-character `affinityTiers` override (when set), so the
     * fan SPA never has to compute tier-from-score itself. The legacy
     * `level` 0-5 system above is preserved for the RelationshipDetail
     * page that still shows the old progress.
     */
    tierV2: z.number().int().min(1).max(10).optional(),
    tierV2Name: z.string().optional(),
    tierV2Description: z.string().optional(),
    unlocks: z.array(z.object({
        level: z.number().int().min(1).max(5),
        levelName: LevelNameSchema,
        /** Conversation-starter prompt the fan can tap on the
         *  RelationshipDetail to seed a chat. */
        conversationStarter: z.string(),
        /** Short character-POV quote, displayed on the unlock card.
         *  When the operator has uploaded a customMessage for this
         *  level via character.fanLevelGifts, it overrides the
         *  default quote here. */
        characterQuote: z.string(),
        /** Single emoji "sticker" representing the unlock — the
         *  default fallback when the operator hasn't uploaded an
         *  image sticker. */
        stickerEmoji: z.string(),
        /** Iter 31 — operator-uploaded sticker image URL for this
         *  level threshold (when set). UI swaps the emoji for the
         *  image when present. */
        stickerUrl: z.string().nullable(),
        /** Iter 31 — operator-uploaded voice clip URL for this level
         *  threshold (when set). UI shows a play button when present. */
        voiceUrl: z.string().nullable(),
    })),
});
